import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  Stack,
  Paper,
  alpha,
  IconButton,
} from "@mui/material";
import { Close, InfoOutlined, MoveToInbox } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { getCaSettingByKey } from "../../../../builders/settings/CaSetting";
import { StyledMenuItem } from "../../../utils/GroupedSelect";
import { styleForTextField } from "../../../utils/TextField/TextField";
import { StyledIconButton } from "../../../utils/Icon";
import {
  isCleanValue,
  LIMITATION_TYPES,
} from "../../../../utils/static/limitation";
import { IA_RATES } from "../FaRegister/rules";

const initialFormData = {
  assetName: "",
  categoryId: "",
  caType: "",
  noOfYears: "",
  cost: "",
  principalPaid: [],
  initialAllowanceRate: "",
};

export const hasDefaultInitialAllowanceRate = (caType) =>
  Object.hasOwn(IA_RATES, caType);

export const getDefaultInitialAllowanceRate = (caType, caSettings) => {
  if (hasDefaultInitialAllowanceRate(caType)) {
    return Number((Number(IA_RATES[caType]) * 100).toFixed(2));
  }

  const settingRate = getCaSettingByKey(caSettings, caType)?.IA;
  return settingRate ?? "";
};

export const hasInitialAllowance = (caSettings, caType) =>
  Boolean(
    getCaSettingByKey(caSettings, caType)?.allowanceType?.IA === true ||
      hasDefaultInitialAllowanceRate(caType)
  );

const applyInitialAllowanceDefault = (asset, caSettings) => {
  if (!hasInitialAllowance(caSettings, asset.caType)) {
    return {
      ...asset,
      initialAllowanceRate: "",
    };
  }

  if (
    asset.initialAllowanceRate !== "" &&
    asset.initialAllowanceRate !== null &&
    asset.initialAllowanceRate !== undefined
  ) {
    return asset;
  }

  return {
    ...asset,
    initialAllowanceRate: getDefaultInitialAllowanceRate(
      asset.caType,
      caSettings
    ),
  };
};

const AssetInfoDialog = ({
  open,
  onClose,
  assetData,
  onSave,
  caSettings,
  caTypeOptions,
  isCreatingNew,
  categoryOption,
  importableAssets = [],
  onImport,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState(initialFormData);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (assetData && !isCreatingNew) {
      setFormData(applyInitialAllowanceDefault(assetData, caSettings));
    } else {
      setFormData(initialFormData);
    }
  }, [assetData, isCreatingNew, caSettings]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.assetName || !formData.assetName.trim()) {
      newErrors.assetName = "Asset name is required";
    }
    if (!formData.categoryId) {
      newErrors.categoryId = "Category is required";
    }
    if (!formData.caType) {
      newErrors.caType = "CA type is required";
    }
    if (!formData.noOfYears) {
      newErrors.noOfYears = "Number of years is required";
    } else {
      const yearsValue = Number(formData.noOfYears);
      if (!Number.isInteger(yearsValue) || yearsValue <= 0) {
        newErrors.noOfYears = "Number of years must be a positive integer";
      }
    }
    if (!formData.cost) {
      newErrors.cost = "Cost is required";
    } else {
      const costValue = Number(formData.cost);
      if (!Number.isFinite(costValue) || costValue < 0) {
        newErrors.cost = "Cost must be greater than or equal to 0";
      }
    }

    // Validate Initial Allowance Rate if required
    if (
      hasInitialAllowance(caSettings, formData.caType) &&
      !formData.initialAllowanceRate
    ) {
      newErrors.initialAllowanceRate = "Initial Allowance Rate is required";
    } else if (
      formData.initialAllowanceRate &&
      (isNaN(formData.initialAllowanceRate) ||
        formData.initialAllowanceRate < 0 ||
        formData.initialAllowanceRate > 100)
    ) {
      newErrors.initialAllowanceRate =
        "Please enter a valid percentage (0-100)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({
        ...formData,
        noOfYears: Number(formData.noOfYears),
        cost: Number(formData.cost),
        initialAllowanceRate:
          formData.initialAllowanceRate &&
          hasInitialAllowance(caSettings, formData.caType)
            ? parseFloat(formData.initialAllowanceRate)
            : undefined,
      });
      setFormData(initialFormData);
    }
  };

  const handleChange = (field) => (event) => {
    if (field === "caType") {
      const caType = event.target.value;
      setFormData({
        ...formData,
        [field]: caType,
        noOfYears: getCaSettingByKey(caSettings, caType)?.numberOfYears || 1,
        initialAllowanceRate: hasInitialAllowance(caSettings, caType)
          ? getDefaultInitialAllowanceRate(caType, caSettings)
          : "",
      });
    } else if (["cost", "noOfYears"].includes(field)) {
      if (isCleanValue(event.target.value, LIMITATION_TYPES.ONLY_NUMBERS)) {
        setFormData({
          ...formData,
          [field]: event.target.value,
        });
      }
    } else {
      setFormData({
        ...formData,
        [field]: event.target.value,
      });
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "." || e.key === "," || e.key === "e") {
      e.preventDefault();
    }
  };

  const handleCloseDialog = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <ReceiptIcon color='primary' />
          </Box>
          <Box flex={1}>
            <Typography variant='h6' fontWeight={600} color='text.primary'>
              {isCreatingNew ? "Add New Asset" : "Edit Asset Information"}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {isCreatingNew
                ? "Enter the details of the new asset"
                : "Update the asset information"}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Close />
          </IconButton>
        </Paper>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 0 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.01),
            }}
          >
            <Stack spacing={4}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                sx={{ mb: 2 }} // Reduced bottom margin for better spacing
              >
                <Typography variant='h6' fontWeight={600} color='primary.main'>
                  Asset Details
                </Typography>
                {isCreatingNew && (
                  <StyledIconButton
                    icon={MoveToInbox}
                    iconSize='24'
                    onClick={() => {
                      setFormData(initialFormData);
                      onImport();
                    }}
                    tooltip='Import Assets from FA Additions'
                    iconColor='primary.main'
                    badgeContent={importableAssets.length}
                  />
                )}
              </Stack>
              {formData.isImported && (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                    border: "1px solid",
                    borderColor: "warning.main",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <InfoOutlined color='warning' sx={{ mt: 0.2 }} />
                  <Box>
                    <Typography
                      variant='subtitle2'
                      fontWeight={600}
                      color='warning.main'
                    >
                      Imported Asset
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      This asset has been imported. The Asset Name and Cost are
                      locked and cannot be edited.
                    </Typography>
                  </Box>
                </Box>
              )}

              <TextField
                label='Asset Name'
                value={formData.assetName}
                onChange={handleChange("assetName")}
                fullWidth
                error={!!errors.assetName}
                helperText={errors.assetName}
                autoComplete='off'
                InputProps={{
                  startAdornment: (
                    <ReceiptIcon color='primary' sx={{ mr: 1 }} />
                  ),
                  "aria-label": "Asset Name",
                }}
                sx={styleForTextField}
                disabled={formData.isImported}
              />

              <TextField
                select
                label='Asset Category'
                value={formData.categoryId}
                onChange={handleChange("categoryId")}
                fullWidth
                error={!!errors.categoryId}
                helperText={errors.categoryId}
                autoComplete='off'
                InputProps={{
                  "aria-label": "Asset Category",
                }}
                sx={styleForTextField}
              >
                {categoryOption.map((cat) => (
                  <StyledMenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </StyledMenuItem>
                ))}
              </TextField>

              <TextField
                select
                label='CA Type'
                value={formData.caType}
                onChange={handleChange("caType")}
                fullWidth
                error={!!errors.caType}
                helperText={errors.caType}
                autoComplete='off'
                InputProps={{
                  "aria-label": "CA Type",
                }}
                sx={styleForTextField}
              >
                {caTypeOptions.map((type) => (
                  <StyledMenuItem
                    key={type}
                    value={type}
                    title={
                      type === "Other"
                        ? "Specify in asset name if not listed"
                        : undefined
                    }
                  >
                    {type}
                  </StyledMenuItem>
                ))}
              </TextField>

              <TextField
                label='No. of years of claim'
                type='number'
                value={formData.noOfYears}
                onChange={handleChange("noOfYears")}
                onKeyDown={handleKeyDown}
                fullWidth
                error={!!errors.noOfYears}
                helperText={errors.noOfYears}
                autoComplete='off'
                InputProps={{
                  inputProps: { min: 1 },
                  "aria-label": "Number of Years",
                }}
                sx={styleForTextField}
                disabled={
                  getCaSettingByKey(caSettings, formData["caType"])
                    ?.numberOfYears !== null
                }
              />

              <TextField
                label='Cost'
                type='number'
                value={formData.cost}
                onChange={handleChange("cost")}
                onKeyDown={handleKeyDown}
                fullWidth
                error={!!errors.cost}
                helperText={
                  errors.cost ||
                  "Cost of asset, excluding interest in functional currency"
                }
                autoComplete='off'
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mr: 1 }}
                    >
                      $
                    </Typography>
                  ),
                  inputProps: {
                    min: 0,
                    step: "1",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  },
                  "aria-label": "Cost",
                }}
                sx={styleForTextField}
                disabled={formData.isImported}
              />

              {/* Initial Allowance Rate - only show when CA type has IA enabled */}
              {hasInitialAllowance(caSettings, formData.caType) && (
                <TextField
                  label='Initial Allowance Rate'
                  type='number'
                  value={formData.initialAllowanceRate}
                  onChange={handleChange("initialAllowanceRate")}
                  fullWidth
                  error={!!errors.initialAllowanceRate}
                  helperText={
                    errors.initialAllowanceRate ||
                    "Enter the initial allowance rate as a percentage (0-100)"
                  }
                  autoComplete='off'
                  InputProps={{
                    endAdornment: (
                      <Typography
                        variant='body1'
                        color='text.secondary'
                        sx={{ ml: 1 }}
                      >
                        %
                      </Typography>
                    ),
                    inputProps: { min: 0, max: 100, step: "0.01" },
                    "aria-label": "Initial Allowance Rate",
                  }}
                  sx={styleForTextField}
                />
              )}
            </Stack>
          </Paper>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2, justifyContent: "flex-end" }}>
        <Button
          onClick={handleCloseDialog}
          variant='outlined'
          color='primary'
          sx={{
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} variant='contained' color='primary'>
          {isCreatingNew ? "Add Asset" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetInfoDialog;
